import React, { useRef, useEffect, useState } from "react";
import { withFixedWebmDuration } from "../../utils/recordingUtils";

/**
 * Screen Capture Recorder Component
 * Records gameplay screen capture using getDisplayMedia API
 */
const ScreenCaptureRecorder = React.forwardRef(({ onRecordingStart, onRecordingStop, existingStream, getExistingStream, onStopRequested }, ref) => {
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const stopCompletionRef = useRef(null);
  const chunksRef = useRef([]);
  const finalizedRef = useRef(false);
  const recorderMimeTypeRef = useRef("video/webm");
  const stopFinalizeTimeoutRef = useRef(null);
  const startedAtRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);
  const selectSupportedMimeType = (stream) => {
    const hasAudioTrack = (stream?.getAudioTracks?.() || []).length > 0;
    const isFirefox = /firefox/i.test(navigator.userAgent);

    // Firefox is sensitive to explicit codec strings in some display-capture scenarios.
    // Let the browser choose defaults there.
    if (isFirefox) {
      return "";
    }

    const candidates = hasAudioTrack
      ? [
          "video/webm;codecs=vp8,opus",
          "video/webm;codecs=vp8",
          "video/webm;codecs=vp9",
          "video/webm",
        ]
      : [
          "video/webm;codecs=vp8",
          "video/webm;codecs=vp9",
          "video/webm",
        ];

    return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || "";
  };

  const clearStopFinalizeTimeout = () => {
    if (stopFinalizeTimeoutRef.current) {
      clearTimeout(stopFinalizeTimeoutRef.current);
      stopFinalizeTimeoutRef.current = null;
    }
  };

  const finalizeRecording = async (source = "unknown") => {
    if (finalizedRef.current) {
      return;
    }
    finalizedRef.current = true;
    clearStopFinalizeTimeout();

    const finalMimeType = recorderMimeTypeRef.current || "video/webm";
    const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
    const rawBlob = new Blob(chunksRef.current, { type: finalMimeType });
    const blob = await withFixedWebmDuration(rawBlob, durationMs);

    if (blob.size === 0) {
      const emptyErr = new Error("Gameplay recording is empty (0 bytes).");
      console.error("[ScreenCaptureRecorder] empty gameplay blob", emptyErr);
      setError(emptyErr.message);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (stopCompletionRef.current) {
        stopCompletionRef.current.reject(emptyErr);
        stopCompletionRef.current = null;
      }
      throw emptyErr;
    }

    try {
      if (onRecordingStop) {
        await onRecordingStop({
          videoBlob: blob,
          mimeType: finalMimeType,
        });
      }
    } catch (callbackErr) {
      if (stopCompletionRef.current) {
        stopCompletionRef.current.reject(callbackErr);
        stopCompletionRef.current = null;
      }
      throw callbackErr;
    } finally {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    }

    if (stopCompletionRef.current) {
      stopCompletionRef.current.resolve();
      stopCompletionRef.current = null;
    }
  };

  // Start recording
  const startRecording = async (providedStream = null) => {
    try {
      setError(null);

      // Get stream from various sources, checking validity
      let stream = providedStream;
      
      // If no provided stream, try to get from getter function
      if (!stream && getExistingStream) {
        stream = getExistingStream();
      }
      
      // If still no stream, try existingStream prop
      if (!stream) {
        stream = existingStream;
      }

      // Validate stream is still active before using it
      if (stream) {
        const videoTracks = stream.getVideoTracks();
        if (videoTracks.length === 0 || videoTracks[0].readyState !== 'live') {
          // Stream is no longer active, clear it and request a new one
          console.warn("Existing screen capture stream is no longer active, requesting new stream");
          stream = null;
        }
      }

      // If no valid stream provided, request screen capture
      if (!stream) {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            mediaSource: "screen", // or "window", "browser"
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
      }

      streamRef.current = stream;
      stopCompletionRef.current = null;
      chunksRef.current = [];
      finalizedRef.current = false;
      clearStopFinalizeTimeout();

      // Handle user stopping screen share
      if (stream.getVideoTracks().length > 0) {
        stream.getVideoTracks()[0].onended = () => {
          stopRecording();
          // Notify parent that recording should stop (this will call RecordingManager.stopRecording)
          if (onStopRequested) {
            onStopRequested();
          }
        };
      }

      // Setup MediaRecorder with a broadly supported mime type.
      // VP8 WebM is accepted by more processing pipelines than VP9.
      const supportedMimeType = selectSupportedMimeType(stream);
      const mediaRecorder = supportedMimeType
        ? new MediaRecorder(stream, { mimeType: supportedMimeType })
        : new MediaRecorder(stream);
      recorderMimeTypeRef.current = mediaRecorder.mimeType || supportedMimeType || "video/webm";

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        try {
          await finalizeRecording("onstop");
        } catch (finalizeErr) {
          console.error("Screen recorder finalization failed:", finalizeErr);
        }
      };

      mediaRecorder.onerror = (event) => {
        const errMessage = event?.error?.message || "Screen recorder failed unexpectedly.";
        console.error("Screen recorder error:", event?.error || event);
        setError(errMessage);
        if (stopCompletionRef.current) {
          stopCompletionRef.current.reject(new Error(errMessage));
          stopCompletionRef.current = null;
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      startedAtRef.current = Date.now();
      // Emit chunks every second to avoid empty-final-blob edge cases.
      mediaRecorder.start(1000);
      setIsRecording(true);

      if (onRecordingStart) {
        onRecordingStart();
      }
    } catch (err) {
      console.error("Error starting screen capture:", err);
      setError(err.message || "Failed to start screen capture");
      setIsRecording(false);

      // If user denied permission, provide helpful error
      if (err.name === "NotAllowedError") {
        setError("Screen capture permission denied. Please allow screen sharing to record gameplay.");
      }
    }
  };

  // Stop recording
  const stopRecording = () => {
    try {
      // Stop MediaRecorder
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        const completionPromise = new Promise((resolve, reject) => {
          stopCompletionRef.current = { resolve, reject };
        });
        try {
          mediaRecorderRef.current.requestData();
        } catch (requestDataErr) {}
        stopFinalizeTimeoutRef.current = setTimeout(async () => {
          if (!finalizedRef.current) {
            try {
              await finalizeRecording("stop-timeout");
            } catch (timeoutFinalizeErr) {
              console.error("Screen recorder forced finalization failed:", timeoutFinalizeErr);
            }
          }
        }, 3000);
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        return completionPromise;
      }

      const completionPromise = new Promise((resolve, reject) => {
        stopCompletionRef.current = { resolve, reject };
      });
      finalizeRecording("inactive-stop")
        .catch((err) => {
          console.error("Screen recorder inactive finalization failed:", err);
        });
      setIsRecording(false);
      return completionPromise;
    } catch (err) {
      console.error("Error stopping screen capture:", err);
      setError(err.message || "Failed to stop screen capture");
      return Promise.reject(err);
    }
  };

  // Expose start/stop methods via ref
  React.useImperativeHandle(ref, () => ({
    start: startRecording,
    stop: stopRecording,
    isRecording: () => isRecording,
    getStream: () => streamRef.current,
  }));

  return (
    <div className="screen-capture-recorder" style={{ display: "none" }}>
      {error && <div className="error">Error: {error}</div>}
    </div>
  );
});

export default ScreenCaptureRecorder;

